import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { X } from "lucide-react";
import { api, resolveImg } from "../api";
import { toast } from "sonner";

const Cart = () => {
  const { items, remove, setQty, subtotal, clear } = useCart();
  const [checkingOut, setCheckingOut] = useState(false);

  const onCheckout = async () => {
    if (!items.length || checkingOut) return;
    setCheckingOut(true);
    try {
      const { checkout_url } = await api.checkout(
        items.map((i) => ({ id: i.id, quantity: i.qty })),
      );
      window.location.href = checkout_url;
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not start checkout.";
      toast.error(detail);
      setCheckingOut(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="px-6 md:px-10 max-w-[900px] mx-auto py-16 text-center">
        <span className="upper font-serif text-[color:var(--ink-mute)]">Cart</span>
        <h1 className="font-serif text-6xl mt-6">Empty for now.</h1>
        <p className="upper-sm font-serif text-[color:var(--ink-mute)] mt-4">Take a look at the shop.</p>
        <Link to="/shop" className="upper font-serif tlink mt-8 inline-block">Browse shop →</Link>
      </div>
    );
  }

  return (
    <div className="px-6 md:px-10 max-w-[1100px] mx-auto">
      <div className="pt-2 pb-8">
        <span className="upper font-serif text-[color:var(--ink-mute)]">Cart — Your selection</span>
      </div>

      <div className="grid grid-cols-12 gap-10">
        <ul className="col-span-12 md:col-span-8 divide-y divide-[color:var(--line)] border-y border-[color:var(--line)]">
          {items.map((i) => (
            <li key={i.id} className="py-6 grid grid-cols-12 gap-4 items-center">
              <div className="col-span-3 md:col-span-2 aspect-[3/4] bg-[color:var(--paper-2)] overflow-hidden">
                {i.cover ? (
                  <img src={resolveImg(i.cover)} alt={i.title} className="w-full h-full object-cover" />
                ) : null}
              </div>
              <div className="col-span-9 md:col-span-6">
                <p className="upper font-serif">{i.title}</p>
                <p className="upper-sm font-serif text-[color:var(--ink-mute)] mt-1">{i.type === "film" ? "Digital rental" : "Print item"}</p>
              </div>
              <div className="col-span-6 md:col-span-2 inline-flex items-center border border-[color:var(--line)] w-max">
                <button onClick={() => setQty(i.id, i.qty - 1)} className="px-2 py-1 font-serif">−</button>
                <span className="px-3 upper-sm font-serif counter">{i.qty}</span>
                <button onClick={() => setQty(i.id, i.qty + 1)} className="px-2 py-1 font-serif">+</button>
              </div>
              <div className="col-span-6 md:col-span-2 flex items-center justify-end gap-3">
                <span className="font-serif counter">€{(i.price * i.qty).toFixed(2)}</span>
                <button onClick={() => remove(i.id)} className="text-[color:var(--ink-mute)] hover:text-[color:var(--ink)]"><X className="w-4 h-4" /></button>
              </div>
            </li>
          ))}
        </ul>

        <aside className="col-span-12 md:col-span-4">
          <div className="border border-[color:var(--line)] p-6 space-y-4">
            <p className="upper-sm font-serif text-[color:var(--ink-mute)]">Summary</p>
            <div className="flex items-baseline justify-between"><span className="upper font-serif">Subtotal</span><span className="font-serif counter">€{subtotal.toFixed(2)}</span></div>
            <div className="flex items-baseline justify-between upper-sm font-serif text-[color:var(--ink-mute)]"><span>Shipping</span><span>At checkout</span></div>
            <div className="border-t border-[color:var(--line)] pt-4 flex items-baseline justify-between">
              <span className="font-serif text-2xl">Total</span>
              <span className="font-serif text-2xl counter">€{subtotal.toFixed(2)}</span>
            </div>
            <button
              onClick={onCheckout}
              disabled={checkingOut}
              className="btn-sober w-full justify-center disabled:opacity-50"
              data-testid="cart-checkout-button"
            >
              {checkingOut ? "Redirecting to Stripe…" : "Checkout"}
            </button>
            <button onClick={clear} className="upper-sm font-serif tlink tlink-mute w-full text-center">Clear cart</button>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default Cart;
